import { useQueryClient, UseQueryResult } from "react-query";
import { Updater } from "react-query/types/core/utils";

/**
 * @description Hook to update the React Query cache using the key to find the query data
 * @param {string | (number | string)[]} key - The key of the query to update
 * @returns {object} The key, current query data and the function to update the query data
 * @example
 * const { currentData, setQueryData } = useUpdateQueryData([GET_USER_TASKS, userId]);
 * setQueryData((oldData) => {
 *   return {
 *     ...oldData,
 *     data: oldData.data.map((task) => task.id === taskId ? { ...task, completed: true } : task),
 *   };
 * });
 */
function useUpdateQueryData<TData>(key: string | (number | string)[]) {
  if (!key) {
    throw new Error("Key is required");
  }

  type QueryFnData = UseQueryResult<TData>;

  const queryClient = useQueryClient();
  const currentData: QueryFnData | undefined = queryClient.getQueryData(key);

  return {
    key,
    currentData: currentData || null,
    setQueryData: (fn: Updater<QueryFnData | undefined, QueryFnData>) =>
      queryClient.setQueryData(key, fn),
  };
}

export default useUpdateQueryData;
